import {
  assetIdLikeToIdentifier,
  Builder,
  EVM_NATIVE_TOKEN_PLACEHOLDER_ADDRESS,
  evmNativeAssetIdForChainRef,
} from "@repo/caip";
import { queryOptions } from "@tanstack/react-query";
import { queryKeys, withSessionQueryScope } from "~/lib/query-keys";
import { getBrowserPlatformApiClient } from "~/lib/trpc/browser-client";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";

export type CashClassificationClient = {
  readonly v3: {
    readonly assets: Pick<
      PlatformApiTrpcClient["v3"]["assets"],
      "cashClassification"
    >;
  };
};
type CashClassificationResponse = Awaited<
  ReturnType<
    CashClassificationClient["v3"]["assets"]["cashClassification"]["query"]
  >
>;
export type CashAssetClassification =
  CashClassificationResponse["items"][number]["classification"];

/** Keep legacy collection IDs at the consumer boundary; the API uses CAIP. */
function toCatalogAssetId(assetId: string): string {
  const asset = assetIdLikeToIdentifier(assetId);
  if (
    asset.chainNamespace === "eip155" &&
    asset.assetReference.toLowerCase() === EVM_NATIVE_TOKEN_PLACEHOLDER_ADDRESS
  ) {
    return evmNativeAssetIdForChainRef(`eip155:${asset.chainReference}`);
  }
  return Builder.buildAssetId(
    { namespace: asset.chainNamespace, reference: asset.chainReference },
    { namespace: asset.assetNamespace, reference: asset.assetReference },
  );
}

export function getCashClassificationQueryOptions(input: {
  readonly assetIds: readonly string[];
  readonly sessionScopeToken: string;
  readonly platformApi?: CashClassificationClient;
}) {
  const assetIds = [...new Set(input.assetIds)].sort();
  const platformApi = input.platformApi ?? getBrowserPlatformApiClient();
  return queryOptions({
    queryKey: withSessionQueryScope(
      queryKeys.assetDomain.cashClassification(assetIds),
      {
        scopeToken: input.sessionScopeToken,
      },
    ),
    queryFn: async ({ signal }) => {
      const aliases = new Map(assetIds.map((id) => [id, toCatalogAssetId(id)]));
      const requested = [...new Set(aliases.values())].sort();
      const items = new Map<
        string,
        CashClassificationResponse["items"][number]
      >();
      const revisions: string[] = [];
      // A failed batch publishes nothing. Each batch is coherent, but the full
      // lookup is not a database snapshot across requests.
      for (let offset = 0; offset < requested.length; offset += 256) {
        signal.throwIfAborted();
        const batch = requested.slice(offset, offset + 256);
        const response = await platformApi.v3.assets.cashClassification.query(
          { assetIds: batch },
          { signal },
        );
        signal.throwIfAborted();
        const remaining = new Set(batch);
        for (const item of response.items) {
          if (!remaining.delete(item.assetId)) {
            throw new Error("cash_classification_response_scope_mismatch");
          }
          items.set(item.assetId, item);
        }
        if (remaining.size > 0)
          throw new Error("cash_classification_response_incomplete");
        revisions.push(response.revision);
      }
      return {
        revisions,
        items: [...aliases].map(([assetId, catalogId]) => {
          const item = items.get(catalogId);
          if (!item) throw new Error("cash_classification_response_incomplete");
          return { ...item, assetId };
        }),
      };
    },
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: true,
  });
}
